import { TitleCasePipe } from '@angular/common'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ButtonModule } from 'primeng/button'
import { CardModule } from 'primeng/card'
import { DividerModule } from 'primeng/divider'
import { InputText } from 'primeng/inputtext'
import { PaginatorModule } from 'primeng/paginator'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { RippleModule } from 'primeng/ripple'
import { Select } from 'primeng/select'
import { TagModule } from 'primeng/tag'
import { debounceTime, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { EmptyPageComponent } from 'app/_shared/components/empty-page/empty-page.component'
import { MobileOrdersFiltersComponent } from 'app/_shared/components/mobile-orders-filters/mobile-orders-filters.component'
import { Order } from 'app/_shared/interfaces/Order'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { DisplayDatePipe } from 'app/_shared/pipes/date.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { OrderService } from 'app/_shared/services/order.service'
import { ToastService } from 'app/_shared/services/toast.service'
import { AppConfigService } from 'app/app-config.service'
import {
  DEFAULT_SORT_FIELD,
  DEFAULT_SORT_ORDER,
  getPaymentStatusSeverity,
  getOrderStatusSeverity,
  getOrderStatus,
  OrderStatus,
  SortOption
} from 'app/pages/account/orders/utils/orders.utils'
import { fetchOrderTranslations } from 'app/pages/account/orders/utils/translation-utils'

@Component({
  selector: 'app-orders',
  imports: [
    Select,
    FormsModule,
    ButtonModule,
    CardModule,
    DividerModule,
    RippleModule,
    TagModule,
    RouterLink,
    TitleCasePipe,
    UsdPipe,
    PricePipe,
    ProgressSpinnerModule,
    PaginatorModule,
    ReactiveFormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    DisplayDatePipe,
    EmptyPageComponent,
    ButtonComponent,
    MobileOrdersFiltersComponent,
    InputText
  ],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.scss'
})
export class OrdersComponent implements OnInit, OnDestroy {
  isLoading: boolean = false
  private appConfigService: AppConfigService = inject(AppConfigService)
  private orderService = inject(OrderService)
  private toastService = inject(ToastService)
  private translateService = inject(TranslateService)
  private destroy$: Subject<void> = new Subject<void>()
  private storefrontId: number
  orders: Order[] = []

  searchTerm: string = ''
  selectedSortOption?: SortOption['value']
  sortOptions: SortOption[]
  selectedStatus: OrderStatus
  statusOptions: { label: string; value: OrderStatus }[]

  currentPage: number = 1
  pageSize: number = 5
  totalRecords: number = 0
  searchControl: FormControl = new FormControl<string>('')
  translationsError: string
  mobileFiltersVisible = false

  ngOnInit(): void {
    this.storefrontId = this.appConfigService.getAppConfig?.id

    fetchOrderTranslations(this.translateService)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: translations => {
          this.statusOptions = translations.statusOptions
          this.sortOptions = translations.sortOptions
          this.translationsError = translations.translationsError
        },
        error: err => {
          this.toastService.error(this.translationsError)
          console.error('Translation Error:', err)
        }
      })

    this.searchControl.valueChanges
      .pipe(debounceTime(1000), takeUntil(this.destroy$))
      .subscribe(value => {
        this.searchTerm = value
        this.resetToFirstPage()
        this.fetchOrders(this.selectedSortOption, this.currentPage)
      })

    this.fetchOrders()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  resetToFirstPage(): void {
    this.currentPage = 1
  }

  onSortChange(sortOption: any): void {
    this.resetToFirstPage()
    this.fetchOrders(sortOption, this.currentPage)
  }

  onStatusChange(status: any): void {
    this.selectedStatus = status
    this.resetToFirstPage()
    this.fetchOrders(this.selectedSortOption, this.currentPage)
  }

  onPageChange(event: any): void {
    this.currentPage = event.page + 1 // PrimeNG paginator is zero-based
    this.pageSize = event.rows
    this.fetchOrders(this.selectedSortOption, this.currentPage)
  }

  fetchOrders(sortOption?: SortOption['value'], page: number = 1): void {
    this.isLoading = true

    const sortField = sortOption?.field || DEFAULT_SORT_FIELD
    const sortOrder = sortOption?.order || DEFAULT_SORT_ORDER

    this.orderService
      .getOrders({
        storefrontId: this.storefrontId,
        sortField,
        sortOrder,
        status: this.selectedStatus,
        searchTerm: this.searchTerm,
        page,
        pageSize: this.pageSize
      })
      .subscribe({
        next: response => {
          this.orders = response.data.data
          this.totalRecords = response.data.total
          this.isLoading = false
        },
        error: err => {
          this.isLoading = false
          this.toastService.error(err.error.message)
        }
      })
  }

  onMobileFiltersApplied(): void {
    this.resetToFirstPage()
    this.fetchOrders(this.selectedSortOption, this.currentPage)
  }

  getOrderStatus = getOrderStatus
  getOrderStatusSeverity = getOrderStatusSeverity
  getPaymentStatusSeverity = getPaymentStatusSeverity
}
